import React, { useEffect, useMemo, useState } from 'react';
import { Combobox } from '../combobox';
import { GoalDto } from '@api/FitnessActivityClient';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';

export interface GoalSelectProps {
    value?: number;
    onChange: (goalId?: number) => void;
    communityId?: number;
    label?: string;
    helperText?: string;
}

export const GoalSelect: React.FC<GoalSelectProps> = ({
    value,
    onChange,
    communityId,
    label = 'Linked goal',
    helperText
}) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState<GoalDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<GoalDto | null>(null);

    useEffect(() => {
        let active = true;
        const fetchGoals = async () => {
            setLoading(true);
            try {
                const response =
                    await fitnessActivityClient.admin.communityGoalAdminControllerGetGoals(
                        {
                            search: searchTerm || undefined,
                            communityId,
                            size: 50
                        }
                    );
                if (active) {
                    setOptions(response.data.content);
                }
            } catch (error) {
                console.error('Error fetching goals:', error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        const timerId = setTimeout(fetchGoals, 300);
        return () => {
            active = false;
            clearTimeout(timerId);
        };
    }, [searchTerm, communityId]);

    useEffect(() => {
        if (value == null) {
            setSelected(null);
            return;
        }
        if (selected?.id === value) {
            return;
        }
        const fromOptions = options.find((g) => g.id === value);
        setSelected(
            fromOptions ?? ({ id: value, key: `Goal #${value}` } as GoalDto)
        );
    }, [value, options]);

    const mergedOptions = useMemo(() => {
        if (selected && !options.some((o) => o.id === selected.id)) {
            return [selected, ...options];
        }
        return options;
    }, [selected, options]);

    return (
        <Combobox
            label={label}
            helperText={helperText}
            placeholder="Select goal"
            searchPlaceholder="Search goals..."
            options={mergedOptions}
            getOptionLabel={(option) => option.key || `Goal #${option.id}`}
            getOptionKey={(option) => option.id}
            value={selected}
            onChange={(newValue) => {
                setSelected(newValue);
                onChange(newValue?.id);
            }}
            onInputChange={setSearchTerm}
            loading={loading}
            renderOption={(option) => (
                <div>
                    <p className="text-sm font-semibold">{option.key}</p>
                    {option.owner?.name && (
                        <p className="text-xs text-muted-foreground">
                            {option.owner.name} · {option.recurrence}
                        </p>
                    )}
                </div>
            )}
        />
    );
};
